import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE } from '@/lib/typography';

export function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className={cn('block', FORM_LABEL_TYPE)}>
      {children}
      {required ? (
        <span className="ml-0.5 text-ds-red-ink" aria-hidden="true">
          *
        </span>
      ) : null}
    </label>
  );
}

export function FieldHint({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className={cn('mt-s1', FORM_HINT_TYPE)}>
      {children}
    </p>
  );
}

export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className={cn('mt-s1 flex items-start gap-s1', FORM_ERROR_TYPE)}>
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  hintId,
  errorId,
  children,
  className,
}: {
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  hintId?: string;
  errorId?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      {label ? (
        <FieldLabel htmlFor={htmlFor} required={required}>
          {label}
        </FieldLabel>
      ) : null}
      <div className={cn(label && 'mt-s1')}>{children}</div>
      {hint && !error ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}
